<script setup lang="ts">
import type { KitResponse } from "mws-catalog-ts-sdk";
import type { AdminTeamResponse, SeasonDto } from "mws-sports-ts-sdk";
import { ref } from "vue";

import CardVue from "@/common/components/card/CardVue.vue";
import PaginatedTable from "@/common/components/table/PaginatedTable.vue";
import type { TableColumn } from "@/common/components/table/types";
import { useListKitMockupsQuery } from "@/kits/api/queries";
import CreateKitModal from "@/kits/components/CreateKitModal.vue";
import KitActionMenu from "@/kits/components/KitActionMenu.vue";
import TeamSeasonSelect from "@/teams/components/TeamSeasonSelect.vue";

definePage({
  meta: {
    auth: ["Kits.Read"],
  },
});

defineProps<{
  team: AdminTeamResponse;
}>();

const columns: TableColumn<KitResponse>[] = [
  {
    key: "name",
    label: "Name",
    type: "link",
    pathConstructor: (item) => `/kits/${item.id}/details`,
  },
  {
    key: "type",
    label: "Type",
    type: "text",
  },
  {
    key: "colour",
    label: "Colour",
    type: "text",
  },
  {
    key: "kitPlayerPosition",
    label: "Player Position",
    type: "text",
  },
  {
    key: "description",
    label: "Description",
    type: "text",
  },
  { key: "isArchived", label: "Archived", type: "bool" },
];

const onSelectSeason = (
  season: SeasonDto | null,
  filters: {
    teamId?: string;
    seasonId?: string;
    leagueId?: string;
    rosterId?: string;
    includeArchived?: boolean;
  },
) => {
  if (!season) {
    filters.seasonId = undefined;
    filters.leagueId = undefined;

    return;
  }
  filters.seasonId = season.id;
  filters.leagueId = season.league?.id;
};

const isKitCreateModalOpen = ref(false);
</script>

<template>
  <CardVue>
    <div class="flex flex-row items-center gap-2 justify-end" />
    <PaginatedTable
      data-cy="team-details-kits"
      :columns="columns"
      :collection-query-fn="useListKitMockupsQuery"
      :collection-options="{
        initialFilters: {
          teamId: team.id,
        },
      }"
      main-button="Create"
      @main-button-click="isKitCreateModalOpen = true"
    >
      <template #filters="{ filters }">
        <TeamSeasonSelect
          :team-id="team.id"
          placeholder="All seasons"
          @select="onSelectSeason($event, filters)"
        />
        <FormKit
          v-model="filters.isArchived"
          type="checkbox"
          label="Show Archived"
        />
      </template>

      <template #rowActions="{ item }">
        <KitActionMenu :kit="item" />
      </template>
    </PaginatedTable>
    <CreateKitModal
      v-model:is-open="isKitCreateModalOpen"
      :team-id="team.id"
      :visit-kit-on-create="true"
    />
  </CardVue>
</template>
